import { useState } from "react";
import { useAuth } from "../lib/AuthContext";
import { COLORS } from "../lib/colors";
import SearchTab from "./SearchTab";
import UploadTab from "./UploadTab";
import DashboardTab from "./DashboardTab";

const TABS = [
  { key: "search", label: "Search" },
  { key: "upload", label: "Upload CVs" },
  { key: "dashboard", label: "Dashboard" },
];

export default function AppShell() {
  const { user, profile, signOut, schools, isPlatformAdmin, schoolId, switchSchool } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");

  const isStaging = import.meta.env.MODE === "staging";

  return (
    <div style={{
      fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
      backgroundColor: COLORS.bg,
      minHeight: "100vh",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

      {/* Staging Banner */}
      {isStaging && (
        <div style={{
          backgroundColor: "#F59E0B",
          color: "#1A1917",
          textAlign: "center",
          padding: "4px 0",
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: "0.05em",
        }}>
          STAGING ENVIRONMENT
        </div>
      )}

      {/* Header */}
      <div style={{
        backgroundColor: COLORS.surface,
        borderBottom: `1px solid ${COLORS.border}`,
        padding: "0 24px",
        position: "sticky",
        top: 0,
        zIndex: 100,
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 56,
          maxWidth: 1400,
          margin: "0 auto",
        }}>
          {/* Left: Logo + Tabs */}
          <div className="header-left" style={{ display: "flex", alignItems: "center", gap: 24 }}>
            {/* Logo */}
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8, backgroundColor: COLORS.accent,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#fff", fontWeight: 700, fontSize: 15,
              }}>R</div>
              <span style={{ fontWeight: 700, fontSize: 16, color: COLORS.text, letterSpacing: "-0.02em" }}>
                Relay
              </span>
              <span style={{
                fontSize: 11, fontWeight: 600, color: COLORS.accent,
                backgroundColor: COLORS.accentLight,
                padding: "2px 8px", borderRadius: 4,
              }}>
                Careers
              </span>
            </div>

            {/* Tab Nav */}
            <div style={{ display: "flex", gap: 4 }}>
              {TABS.map(t => (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  style={{
                    padding: "6px 16px",
                    borderRadius: 6,
                    border: "none",
                    fontSize: 13,
                    fontWeight: 600,
                    fontFamily: "inherit",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    backgroundColor: activeTab === t.key ? COLORS.accent : "transparent",
                    color: activeTab === t.key ? "#fff" : COLORS.textSecondary,
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Right: School Selector + User + Logout */}
          <div className="header-right" style={{ display: "flex", alignItems: "center", gap: 16 }}>
            {isPlatformAdmin && schools.length > 0 && (
              <select
                value={schoolId || ""}
                onChange={(e) => switchSchool(e.target.value || null)}
                style={{
                  padding: "6px 28px 6px 10px",
                  borderRadius: 6,
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: COLORS.surface,
                  color: schoolId ? COLORS.text : COLORS.textMuted,
                  fontSize: 13,
                  fontWeight: 500,
                  fontFamily: "inherit",
                  cursor: "pointer",
                  appearance: "none",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236B6860' d='M3 5l3 3 3-3'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 8px center",
                  minWidth: 160,
                }}
              >
                <option value="">All schools</option>
                {schools.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            )}
            <span style={{ fontSize: 13, color: COLORS.textSecondary }}>
              {user?.email}
            </span>
            <span style={{
              fontSize: 11, fontWeight: 600, color: COLORS.purple,
              backgroundColor: COLORS.purpleLight,
              padding: "2px 8px", borderRadius: 4,
            }}>
              {profile?.role}
            </span>
            <button
              onClick={signOut}
              style={{
                padding: "6px 14px",
                borderRadius: 6,
                border: `1px solid ${COLORS.border}`,
                backgroundColor: COLORS.surface,
                color: COLORS.textSecondary,
                fontSize: 13,
                fontWeight: 500,
                fontFamily: "inherit",
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "24px 24px 48px" }}>
        {activeTab === "search" && <SearchTab />}
        {activeTab === "upload" && <UploadTab />}
        {activeTab === "dashboard" && <DashboardTab />}
      </div>
    </div>
  );
}
